import { FieldError } from "event-definitions"
import { useState } from "react"

/**
 * Hook to track field-level validation errors for an `ExtendedSelect` component.
 *
 * Listens for `FieldError` events scoped to a specific `selectId` and `type`,
 * and maintains a list of error types for each affected field. This is useful
 * for displaying or handling validation states in `ExtendedSelect` components.
 *
 * @param {string} [selectId="*"] - The ID of the select component to listen to. Defaults to "*" (all).
 * @param {string} [type="*"] - The error type to listen for. Defaults to "*" (all).
 * @returns {{
 *   fieldErrors: Record<string, {
 *     id: string,
 *     field: string,
 *     errorTypes: string[]
 *   }>,
 *   hasFieldError: boolean
 * }} An object with:
 *   - `fieldErrors`: A mapping of field IDs to error metadata.
 *   - `hasFieldError`: A boolean flag indicating if any field has an error.
 */
export function useSelectFieldErrors(selectId = "*", type = "*") {
    const [fieldErrors, setFieldErrors] = useState({})
    const hasFieldError = Object.keys(fieldErrors).length > 0

    FieldError(selectId)(type).useEvent(({ id, field, error, errorType }) => {
        setFieldErrors((prev) => {
            const next = { ...prev }
            if (error) {
                const existingErrorTypes = next[id]?.errorTypes ?? []
                if (!existingErrorTypes.includes(errorType)) {
                    next[id] = {
                        id,
                        field,
                        errorTypes: [...existingErrorTypes, errorType],
                    }
                }
            } else {
                const existingErrorTypes = next[id]?.errorTypes ?? []
                const updatedErrorTypes = existingErrorTypes.filter((e) => e !== errorType)
                if (updatedErrorTypes.length === 0) {
                    delete next[id]
                } else {
                    next[id] = {
                        ...next[id],
                        errorTypes: updatedErrorTypes,
                    }
                }
            }
            return next
        })
    })

    return { fieldErrors, hasFieldError }
}
